// Texterna i docs som React: avtalen (ADR 0100) och resurserna (ADR 0105).
// De använder bara rubriker, stycken, listor, tabeller, fetstil och länkar,
// så en liten läsare räcker.
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/** "Kom igång" blir "kom-igang", för länkar till ett avsnitt. */
export function ankare(rubrik: string): string {
  return rubrik
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** **fetstil** blir <strong>, och [text](/sida) en länk. Sidor i kontot öppnas utan omladdning. */
function rad(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).map((del, i) => {
    if (del.startsWith('**') && del.endsWith('**'))
      return <strong key={i}>{del.slice(2, -2)}</strong>;
    const lank = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(del);
    if (!lank) return del;
    const [, etikett = '', till = ''] = lank;
    return till.startsWith('/') ? (
      <Link key={i} to={till}>
        {etikett}
      </Link>
    ) : (
      <a key={i} href={till}>
        {etikett}
      </a>
    );
  });
}

const celler = (r: string) =>
  r
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

export function Markdown({
  text,
  rubrikId,
  ankarePaRubriker = false,
}: {
  text: string;
  rubrikId?: string;
  /** Ge varje ##-rubrik ett id, så att innehållsförteckningen kan länka till den. */
  ankarePaRubriker?: boolean;
}) {
  const block: ReactNode[] = [];
  const rader = text.split('\n');
  for (let i = 0; i < rader.length;) {
    const r = rader[i] ?? '';
    if (!r.trim()) {
      i++;
    } else if (r.startsWith('# ')) {
      block.push(
        <h1 key={i} id={rubrikId}>
          {r.slice(2)}
        </h1>,
      );
      i++;
    } else if (r.startsWith('## ')) {
      const rubrik = r.slice(3);
      block.push(
        <h2 key={i} id={ankarePaRubriker ? ankare(rubrik) : undefined}>
          {rad(rubrik)}
        </h2>,
      );
      i++;
    } else if (r.startsWith('### ')) {
      block.push(<h3 key={i}>{rad(r.slice(4))}</h3>);
      i++;
    } else if (r.startsWith('|')) {
      const tabell: string[] = [];
      while (rader[i]?.startsWith('|')) tabell.push(rader[i++] ?? '');
      const [huvud = '', , ...kropp] = tabell;
      block.push(
        <div key={i} className="tabell-rull" role="region" aria-label="Tabell" tabIndex={0}>
          <table>
            <thead>
              <tr>
                {celler(huvud).map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kropp.map((k, j) => (
                <tr key={j}>
                  {celler(k).map((c, n) => (
                    <td key={n}>{rad(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else if (/^(- |\d+\. )/.test(r)) {
      const numrerad = /^\d+\. /.test(r);
      const punkter: string[] = [];
      while (rader[i] && /^(- |\d+\. )/.test(rader[i] ?? '')) {
        punkter.push((rader[i++] ?? '').replace(/^(- |\d+\. )/, ''));
      }
      const Lista = numrerad ? 'ol' : 'ul';
      block.push(
        <Lista key={i}>
          {punkter.map((p, j) => (
            <li key={j}>{rad(p)}</li>
          ))}
        </Lista>,
      );
    } else {
      const stycke: string[] = [];
      while (rader[i]?.trim() && !/^(#|\||- |\d+\. )/.test(rader[i] ?? '')) {
        stycke.push((rader[i++] ?? '').trim());
      }
      block.push(<p key={i}>{rad(stycke.join(' '))}</p>);
    }
  }
  return <>{block}</>;
}
